/**
 * The reportings a document is tied to in CSRD.
 *
 * A document carries one **principal** reporting and, if it needs them,
 * **secondary** ones. The principal is the one it is about and the one CSRD is
 * kept on; a secondary is another reporting the document also takes figures
 * from. The role is stored, not inferred from the order, because the order is
 * only how they were first picked and something else will have to ask later.
 *
 * Each also keeps the reporting CSRD links it to as its N-1 (its opening
 * balances). That is CSRD's own link, copied here as it stood, so an import can
 * reach the previous year without asking CSRD again.
 *
 * Everything that names a reporting does so by its code, with the id kept as the
 * quick way back: an id belongs to one CSRD database, a code is what names the
 * same reporting in the next exercise.
 */

import {
  isJsonObject,
  readNumber,
  readString,
  type JsonObject,
  type JsonValue,
} from '@/domain/json'
import type { CsrdLink, Section } from '@/domain/section'

export type ReportingRole = 'principal' | 'secondaire'

/** A reporting CSRD lists. */
export interface ListedReporting {
  readonly id: number
  readonly code: string
  readonly label: string
  /** The year its exercise ends in. */
  readonly year: string
  /** `CSRD`, `VSME`, `CBCR`… */
  readonly kind: string
  /** The reporting it takes its opening balances from, when CSRD links one. */
  readonly n1Id: number | null
  readonly n1Code: string
}

/** A reporting of a document, and the part it plays there. */
export interface DocumentReporting extends ListedReporting {
  readonly role: ReportingRole
}

function whole(source: JsonValue | undefined, key: string): number {
  const value = readNumber(source, key, 0)
  return Number.isInteger(value) && value > 0 ? value : 0
}

/**
 * The reportings stored with a document.
 *
 * Forgiving, because a document written by another version, or by hand, has to
 * open: an entry that cannot name a reporting (no id, no code) is dropped, and
 * the rest are settled so there is exactly one principal, first. A stored list
 * that names none, or several, is mended rather than refused.
 */
export function readDocumentReportings(stored: readonly JsonValue[]): DocumentReporting[] {
  const found: DocumentReporting[] = []
  const seen = new Set<string>()

  for (const item of stored) {
    if (!isJsonObject(item)) continue
    const id = whole(item, 'id')
    const code = readString(item, 'code').trim()
    if (!id || !code || seen.has(code)) continue
    seen.add(code)

    const n1Id = whole(item, 'n1Id')
    found.push({
      id,
      code,
      label: readString(item, 'label'),
      year: readString(item, 'year'),
      kind: readString(item, 'kind'),
      role: readString(item, 'role') === 'principal' ? 'principal' : 'secondaire',
      n1Id: n1Id || null,
      n1Code: readString(item, 'n1Code'),
    })
  }

  return settled(found)
}

/** One principal, first; the others as they were. The first one named wins. */
function settled(list: readonly DocumentReporting[]): DocumentReporting[] {
  if (list.length === 0) return []
  const principal = list.find((one) => one.role === 'principal') ?? list[0]!
  return [
    { ...principal, role: 'principal' },
    ...list
      .filter((one) => one !== principal)
      .map((one) => ({ ...one, role: 'secondaire' as const })),
  ]
}

/** A document's reportings as they are stored. */
export function writeDocumentReportings(list: readonly DocumentReporting[]): JsonObject[] {
  return list.map((one) => ({ ...one }))
}

/** The reportings somebody picked, in the order they picked them: the first is the principal. */
export function chooseReportings(picked: readonly ListedReporting[]): DocumentReporting[] {
  return picked.map((one, at) => ({ ...one, role: at === 0 ? 'principal' : 'secondaire' }))
}

/** The reporting the document is about, or undefined when it has none. */
export function principalOf(list: readonly DocumentReporting[]): DocumentReporting | undefined {
  return list.find((one) => one.role === 'principal')
}

/** Make another of the reportings the principal; the previous one becomes a secondary. */
export function makePrincipal(
  list: readonly DocumentReporting[],
  code: string,
): DocumentReporting[] {
  const next = list.find((one) => one.code === code)
  if (!next) return [...list]
  return settled([{ ...next, role: 'principal' }, ...list.filter((one) => one !== next)])
}

export function secondariesOf(list: readonly DocumentReporting[]): DocumentReporting[] {
  return list.filter((one) => one.role === 'secondaire')
}

/**
 * Which new secondary stands for each old one: old code to new code, or empty
 * for none.
 *
 * Suggested, not decided: of the old document's secondaries, each takes the
 * first unused new one of the same kind, in order. The person confirms or
 * changes it, since two reportings of one kind say nothing about which is which.
 */
export function suggestCorrespondence(
  previous: readonly DocumentReporting[],
  next: readonly DocumentReporting[],
): Record<string, string> {
  const free = secondariesOf(next)
  const chosen: Record<string, string> = {}

  for (const old of secondariesOf(previous)) {
    const at = free.findIndex((one) => one.kind === old.kind)
    if (at === -1) {
      chosen[old.code] = ''
    } else {
      chosen[old.code] = free[at]!.code
      free.splice(at, 1)
    }
  }
  return chosen
}

/**
 * The new reporting that replaces each old one, by code: the old principal by
 * the new principal, and an old secondary by the one chosen for it. An old
 * reporting with no counterpart is left out, and its tables stay as they were.
 */
export function replacements(
  previous: readonly DocumentReporting[],
  next: readonly DocumentReporting[],
  correspondence: Readonly<Record<string, string>>,
): Map<string, DocumentReporting> {
  const byCode = new Map(next.map((one) => [one.code, one]))
  const found = new Map<string, DocumentReporting>()

  const was = principalOf(previous)
  const now = principalOf(next)
  if (was && now) found.set(was.code, now)

  for (const old of secondariesOf(previous)) {
    const target = byCode.get(correspondence[old.code] ?? '')
    if (target) found.set(old.code, target)
  }
  return found
}

/** The old secondaries nothing was chosen for. Their tables are left to be reconnected. */
export function leftWithout(
  previous: readonly DocumentReporting[],
  correspondence: Readonly<Record<string, string>>,
): DocumentReporting[] {
  return secondariesOf(previous).filter((old) => !correspondence[old.code])
}

function relinked(link: CsrdLink, to: DocumentReporting): CsrdLink {
  return { ...link, reportingCode: to.code, reportingLabel: to.label }
}

/**
 * The sections, with each table tied to an old reporting tied to its
 * replacement instead. Everything else is returned as it was, and a table whose
 * reporting has no replacement keeps its link.
 */
export function relinkSections(
  sections: readonly Section[],
  by: ReadonlyMap<string, DocumentReporting>,
): Section[] {
  return sections.map((section): Section => {
    if (section.kind === 'group') {
      return { ...section, children: relinkSections(section.children, by) }
    }
    const to =
      section.kind === 'table' && section.csrd ? by.get(section.csrd.reportingCode) : undefined
    if (section.kind === 'table' && section.csrd && to) {
      return { ...section, csrd: relinked(section.csrd, to) }
    }
    return section
  })
}

/** Whether a table's reporting is one the document still has. False for a table to reconnect. */
export function isAttached(link: CsrdLink, list: readonly DocumentReporting[]): boolean {
  return list.length === 0 || list.some((one) => one.code === link.reportingCode)
}
